import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  Heading,
  FormLabel,
  InputGroup,
  FormControl,
  InputLeftElement,
  InputRightElement,
  useTheme,
  FormHelperText,
  Card
} from '@homebotapp/hb-react-component-catalog'
import { useEffect, useState } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'
import { formatAbbrCurrency, formatCurrency, formatNumber } from '../../../../../../helpers/formatters'
import { BucketedMonthlyHomeExpenses, ListingLoanInfo } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT } from '../../../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'

export const MSG = defineMessages({
  cancel: {
    id: 'EstimatedMonthlyPaymentBreakdown.cancel',
    defaultMessage: 'Hide customization'
  },
  principalAndInterest: {
    id: 'EstimatedMonthlyPaymentBreakdown.principalAndInterest',
    defaultMessage: 'Principal & interest'
  },
  principalAndInterestTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.principalAndInterestTermAndRate',
    defaultMessage: '{years} years at {rate}% APR'
  },
  down: {
    id: 'EstimatedMonthlyPaymentBreakdown.down',
    defaultMessage: '{down} down payment'
  },
  editDownPayment: {
    id: 'EstimatedMonthlyPaymentBreakdown.editDownPayment',
    defaultMessage: 'Adjust your down payment'
  },
  editDownPaymentDescription: {
    id: 'EstimatedMonthlyPaymentBreakdown.editDownPaymentDescription',
    defaultMessage: 'Enter a dollar amount or a percent of the {homePrice}\u00a0list\u00a0price'
  },
  customizeThis: {
    id: 'EstimatedMonthlyPaymentBreakdown.customizeThis',
    defaultMessage: 'Customize down payment'
  },
  recalculate: {
    id: 'EstimatedMonthlyPaymentBreakdown.recalculate',
    defaultMessage: 'Re-calculate payment'
  },
  cash: {
    id: 'EstimatedMonthlyPaymentBreakdown.cash',
    defaultMessage: 'Cash'
  },
  percentage: {
    id: 'EstimatedMonthlyPaymentBreakdown.percentage',
    defaultMessage: 'Percentage'
  },
  max: {
    id: 'EstimatedMonthlyPaymentBreakdown.max',
    defaultMessage: 'max'
  },
  assumedMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumedMortgage',
    defaultMessage: 'Assumed mortgage'
  },
  assumedMortgageTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumedMortgageTermAndRate',
    defaultMessage: '{years} years remaining {rate}%'
  },
  secondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.secondMortgage',
    defaultMessage: 'Second mortgage'
  },
  assumableExplainerNoSecondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumableExplainerNoSecondMortgage',
    defaultMessage:
      'Based on a {down}, you can cover the difference between the assumed mortgage and the listing price.'
  },
  assumableExplainerWithSecondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumableExplainerWithSecondMortgage',
    defaultMessage: 'Based on a {down}, you’ll need a second mortgage to cover the listing price.'
  },
  downPaymentInline: {
    id: 'EstimatedMonthlyPaymentBreakdown.downPaymentInline',
    defaultMessage: '{down} down payment'
  },
  secondMortgageTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.secondMortgageTermAndRate',
    defaultMessage: '{years} years at {rate}%'
  }
})

export interface EstimatedMonthlyPaymentBreakdownPrincipalAndInterestProps {
  homePrice: number
  principalAndInterest: number
  loanInfo?: ListingLoanInfo | null
  setDownPaymentAmountDollars: (downPaymentCash: number) => void
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumableBucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableToggle?: boolean
}

export const EstimatedMonthlyPaymentBreakdownPrincipalAndInterest = ({
  loanInfo,
  homePrice,
  principalAndInterest,
  setDownPaymentAmountDollars,
  assumableBucketedMonthlyHomeExpenses,
  assumableMortgageLoanInfo,
  assumableMortgageSecondLoanInfo,
  assumableToggle = false
}: EstimatedMonthlyPaymentBreakdownPrincipalAndInterestProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const isLight = theme.isLightThemeType

  const [isEditing, setIsEditing] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [showPaymentHint, setShowPaymentHint] = useState(false)
  const [downPaymentCash, setDownPaymentCash] = useState(Number(loanInfo?.downpayment.toFixed(0)) || 0)
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(
    Number((loanInfo?.downpayment && (loanInfo?.downpayment / homePrice) * 100)?.toFixed(2)) || 0
  )

  useEffect(() => {
    if (loanInfo?.downpayment || loanInfo?.downpayment === 0) {
      setDownPaymentCash(Number(loanInfo?.downpayment.toFixed(2)))
      setDownPaymentPercentage(Number(((loanInfo?.downpayment / homePrice) * 100).toFixed(2)))
    }
  }, [loanInfo])

  // @ts-ignore
  const formatPercentage = (value: number) => {
    if (value) {
      return parseFloat(value.toString().replace(/^0+/, ''))
    }
  }

  const handleChangeDownPaymentCash = (cash: number) => {
    const cashString = cash.toString()
    let cashNumber = Number(cashString)
    // Let's not exceed homePrice
    if (cashNumber > homePrice) {
      setShowPaymentHint(true)
      return
    }
    if (cashNumber === 0) {
      setDownPaymentCash(0)
      setDownPaymentPercentage(0)
    } else {
      setDownPaymentCash(cashNumber)
      setDownPaymentPercentage(Number(((cashNumber / homePrice) * 100).toFixed(3)))
    }
    localStorage.removeItem('downPaymentPercentage')
    localStorage.setItem('downPaymentCash', cash.toString())
  }

  const handleChangeDownPaymentPercentage = (percentage: number) => {
    const percentageString = percentage.toString()

    let percentageNumber = Number(parseFloat(percentageString)) || 0
    const decimalPointIndex = percentageString.indexOf('.')

    // Let's not exceed 100%
    if (percentageNumber > 100) {
      setShowPaymentHint(true)
      return
    }

    // Let's not have more than 3 decimal places
    if (decimalPointIndex !== -1 && percentageString.length - decimalPointIndex - 1 > 3) {
      return
    }

    setDownPaymentPercentage(Number(percentageNumber.toFixed(3)))
    localStorage.removeItem('downPaymentCash')
    localStorage.setItem('downPaymentPercentage', percentage.toString())
    setDownPaymentCash(Number(((homePrice * percentageNumber) / 100).toFixed(2)))
  }

  const handleRecalculate = () => {
    setIsCalculating(true)

    try {
      setDownPaymentAmountDollars(downPaymentCash)
    } catch {
    } finally {
      // Delay the state change to give the user a visual cue that the calculation is happening
      setTimeout(() => {
        setIsCalculating(false)
      }, 400)
    }
  }

  const showSecondMortgage = Boolean(
    assumableToggle &&
      assumableBucketedMonthlyHomeExpenses?.secondMortgagePrincipalAndInterest &&
      assumableBucketedMonthlyHomeExpenses.secondMortgagePrincipalAndInterest > 0
  )

  return (
    <Card p={3}>
      <Box>
        {assumableToggle &&
          (showSecondMortgage ? (
            <Text>
              {intl.formatMessage(MSG.assumableExplainerWithSecondMortgage, {
                down: (
                  <strong>
                    {intl.formatMessage(MSG.downPaymentInline, { down: formatCurrency(loanInfo?.downpayment || 0) })}
                  </strong>
                )
              })}
            </Text>
          ) : (
            <Text>
              {intl.formatMessage(MSG.assumableExplainerNoSecondMortgage, {
                down: (
                  <strong>
                    {intl.formatMessage(MSG.downPaymentInline, { down: formatCurrency(loanInfo?.downpayment || 0) })}
                  </strong>
                )
              })}
            </Text>
          ))}
      </Box>
      <Flex justify='space-between' align='center' mb={2}>
        <Flex gap={2} align='center'>
          <Box bg='success.700' width={3} height={3} borderRadius='50%' />
          <Heading as='h4' size={['2xs', 'xs', 'sm']}>
            {assumableToggle ? intl.formatMessage(MSG.assumedMortgage) : intl.formatMessage(MSG.principalAndInterest)}
          </Heading>
        </Flex>
        <Text
          mb={0}
          color='neutral.900'
          size={['xs', 'sm', 'md']}
          fontWeight='semibold'
          style={{ fontVariant: 'tabular-nums' }}
        >
          {assumableToggle
            ? formatCurrency(assumableBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest)
            : formatCurrency(principalAndInterest)}
        </Text>
      </Flex>
      <Flex align='baseline' justifyContent='flex-start' direction={['column', 'row']} gap={0}>
        {assumableToggle ? (
          <Box ml={5} />
        ) : (
          <>
            <Text ml={5} as='span' size={['2xs', '2xs', 'sm']}>
              <Text as='span' size={['2xs', '2xs', 'sm']} color='success.700' fontWeight='bold'>
                {intl.formatMessage(MSG.down, {
                  down: formatCurrency(loanInfo?.downpayment || 0)
                })}
              </Text>
            </Text>
            <Text
              as='span'
              mx={'1ch'}
              size={['2xs', '2xs', 'sm']}
              color='success.700'
              fontWeight='bold'
              display={['none', 'initial']}
            >
              ·
            </Text>
          </>
        )}
        <Text ml={[5, 0]} as='span' size={['2xs', '2xs', 'sm']}>
          {assumableToggle
            ? intl.formatMessage(MSG.assumedMortgageTermAndRate, {
                years: assumableMortgageLoanInfo?.termYears,
                rate: assumableMortgageLoanInfo?.interestRatePercent
                  ? parseFloat(assumableMortgageLoanInfo?.interestRatePercent.toFixed(3))
                      .toString()
                      .replace(/(\.\d*?[1-9])0+$/, '$1')
                  : 'unknown'
              })
            : intl.formatMessage(MSG.principalAndInterestTermAndRate, {
                years: loanInfo?.termYears,
                // Trim trailing 0's after the decimal
                rate: loanInfo?.interestRatePercent
                  ? parseFloat(loanInfo?.interestRatePercent.toFixed(3))
                      .toString()
                      .toString()
                      .replace(/(\.\d*?[1-9])0+$/, '$1')
                  : 'unknown'
              })}
        </Text>
      </Flex>

      {/* Second Mortgage, optional */}

      {showSecondMortgage && (
        <>
          <Flex justify='space-between' align='center' marginY={2}>
            <Flex gap={2} align='center'>
              <Box
                width={3}
                height={3}
                borderRadius='50%'
                bg={theme.isLightThemeType ? 'success.400' : 'success.900'}
              />
              <Heading as='h4' size={['2xs', 'xs', 'sm']}>
                {intl.formatMessage(MSG.secondMortgage)}
              </Heading>
            </Flex>
            <Text
              mb={0}
              color='neutral.900'
              size={['xs', 'sm', 'md']}
              fontWeight='semibold'
              style={{ fontVariant: 'tabular-nums' }}
            >
              {formatCurrency(assumableBucketedMonthlyHomeExpenses?.secondMortgagePrincipalAndInterest)}
            </Text>
          </Flex>

          <Flex align='baseline' justifyContent='flex-start' direction={['column', 'row']} gap={0}>
            <Text ml={5} as='span' size={['2xs', '2xs', 'sm']}>
              {intl.formatMessage(MSG.secondMortgageTermAndRate, {
                years: assumableMortgageSecondLoanInfo?.termYears,
                // Trim trailing 0's after the decimal
                rate: assumableMortgageSecondLoanInfo?.interestRatePercent
                  ? parseFloat(assumableMortgageSecondLoanInfo?.interestRatePercent.toFixed(3))
                      .toString()
                      .toString()
                      .replace(/(\.\d*?[1-9])0+$/, '$1')
                  : 'unknown'
              })}
            </Text>
          </Flex>
        </>
      )}

      <Button
        mt={4}
        size={['sm', 'sm', 'md']}
        width='100%'
        onClick={() => (isEditing ? setIsEditing(false) : setIsEditing(true))}
        tracking={
          isEditing
            ? {
                guid: 'entda6GpxWXRtsufyC3fSU',
                ui_context: HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                descriptor: MSG.cancel.defaultMessage
              }
            : {
                guid: '384ZyHGZwaTJjNFDdszjnU',
                ui_context: HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                descriptor: MSG.customizeThis.defaultMessage
              }
        }
      >
        {isEditing ? intl.formatMessage(MSG.cancel) : intl.formatMessage(MSG.customizeThis)}
      </Button>

      <Flex
        transition='height .2s ease-in-out, margin-top .2s ease-in-out,  padding-top .25s ease-in-out, opacity .1s ease-in-out'
        opacity={isEditing ? 1 : 0}
        height={isEditing ? 'auto' : 0}
        mt={isEditing ? 0 : -4}
        pt={isEditing ? 1 : 4}
        pb={isEditing ? 4 : 0}
        // @ts-ignore
        style={{ inert: !isEditing }}
        zIndex={isEditing ? 1 : -1}
        alignItems='center'
        direction='column'
      >
        <Box pt={1} pb={1} w='100%'>
          <Box
            display='block'
            height='1px'
            borderTopWidth='1px'
            borderTopColor={isLight ? 'blackAlpha.200' : 'whiteAlpha.200'}
            borderStyle='solid'
            mt={[3, 4, 4]}
            mb={[3, 5, 6]}
          />
        </Box>
        <Flex direction='column' alignItems='center'>
          <hgroup data-testid={MSG.editDownPaymentDescription.id}>
            <Heading as='h4' size={['xs', 'sm']} textAlign='center'>
              {intl.formatMessage(MSG.editDownPayment)}
            </Heading>
            <Text mb={0} size={['xs', 'sm']} textAlign='center'>
              <FormattedMessage
                id={MSG.editDownPaymentDescription.id}
                defaultMessage={MSG.editDownPaymentDescription.defaultMessage}
                values={{
                  homePrice: (
                    <Text as='span' size='inherit' fontWeight='600' color='neutral.900'>
                      {formatAbbrCurrency(homePrice)}
                    </Text>
                  )
                }}
              />
            </Text>
          </hgroup>
          <Flex direction='column' maxWidth='240px' alignItems='center' mt={6}>
            <Flex direction='row' gap={2} alignItems='flex-start'>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.cash)}</FormLabel>
                <InputGroup size='sm'>
                  <InputLeftElement pointerEvents='none' mt={'.5px'}>
                    $
                  </InputLeftElement>
                  <Input
                    pl={5}
                    type='text'
                    inputMode='numeric'
                    min={0}
                    max={homePrice}
                    transition='color 0s ease-in-out'
                    value={formatNumber(downPaymentCash ?? 0).toString()}
                    name='down-payment-cash'
                    onChange={e => {
                      const inputValue = e.target.value.replace(/,/g, '')
                      if (inputValue === '' || inputValue === '0' || inputValue === '0.') {
                        handleChangeDownPaymentCash(0)
                      } else {
                        const numericValue = parseInt(inputValue, 10)
                        if (!isNaN(numericValue)) {
                          handleChangeDownPaymentCash(numericValue)
                        }
                      }
                    }}
                    tracking={{
                      guid: 'rFTRcPY1nfPRTj4m6SnUhy',
                      ui_context: HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                      descriptor: 'Down payment cash input field'
                    }}
                  />
                </InputGroup>
                {showPaymentHint && (
                  <FormHelperText fontSize='3xs'>
                    ${formatNumber(homePrice)} {intl.formatMessage(MSG.max)}
                  </FormHelperText>
                )}
              </FormControl>
              <Box height={8} alignContent='center' justifyContent='center' mt={7}>
                <Text as='span'>=</Text>
              </Box>
              <FormControl w='fit-content'>
                <FormLabel size='xs' ml='auto' mr='0' pl={3}>
                  {intl.formatMessage(MSG.percentage)}
                </FormLabel>
                <InputGroup size='sm'>
                  <Input
                    pr={5}
                    min={0}
                    max={100}
                    type='number'
                    inputMode='decimal'
                    textAlign='right'
                    transition='color 0s ease-in-out'
                    value={formatPercentage(downPaymentPercentage)}
                    name='down-payment-percentage'
                    onChange={e => {
                      let inputValue = e.target.value
                      if (inputValue.startsWith('0') && inputValue.length > 4) {
                        inputValue = inputValue.replace(/^0+/, '')
                      }
                      const numericValue = parseFloat(inputValue)
                      if (!isNaN(numericValue)) {
                        handleChangeDownPaymentPercentage(numericValue)
                      } else if (inputValue === '0.') {
                        handleChangeDownPaymentPercentage(numericValue)
                      } else if (inputValue === '') {
                        handleChangeDownPaymentPercentage(0)
                      }
                    }}
                    tracking={{
                      guid: '1DiJwBHoPY1mgks79RpnmN',
                      ui_context: HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                      descriptor: 'Down payment percentage input field'
                    }}
                  />
                  <InputRightElement pointerEvents='none' fontSize='xs' mr={-0.5}>
                    %
                  </InputRightElement>
                </InputGroup>
                {showPaymentHint && (
                  <FormHelperText fontSize='3xs' textAlign='right'>
                    100% {intl.formatMessage(MSG.max)}
                  </FormHelperText>
                )}
              </FormControl>
            </Flex>
            <Button
              mt={4}
              w='100%'
              size='sm'
              colorScheme='primary'
              isLoading={isCalculating}
              onClick={() => handleRecalculate()}
              tracking={{
                guid: '1waH6PE3tN99UNs1zFGykr',
                ui_context: HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                descriptor: MSG.recalculate.defaultMessage
              }}
            >
              {intl.formatMessage(MSG.recalculate)}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
